import React, { useState, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from 'react-bootstrap';
import dayjs from 'dayjs';
import { LineChart, Line, Tooltip, ResponsiveContainer } from 'recharts';
import { Dropdown, DropdownBody, DropdownItem, DropdownToggle } from '../../../../components/dropdown';
import { Checkbox } from '../../../../components/checkbox-or-radio-field';
import Slideout from '../../../../components/slideout/Slideout';
import LoadingSpinner from '../../../../components/loading-spinner';
import { GET_TEAM_TEMPLATE_ANALYTICS } from '../../../../api/templateAnalytics';
import TemplateChartModal from './TemplateChartModal';
import { exportTemplateAnalyticsToCsv } from './exportUtils';
import styles from './styles.module.scss';

interface TemplateAnalyticsSlideoutProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
}

interface TemplateAnalytics {
  id: string;
  name: string;
  format: string;
  totalUses: number;
  lastUsedAt: string | null;
  usageTrend: Array<{ date: string; count: number }>;
}

const TemplateAnalyticsSlideout: React.FC<TemplateAnalyticsSlideoutProps> = ({ isOpen, onClose, teamId }) => {
  const { formatMessage } = useIntl();
  const [dateRange, setDateRange] = useState('last30days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [selectedFormats, setSelectedFormats] = useState<string[]>(['pdf', 'fully_editable', 'restricted']);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateAnalytics | null>(null);
  const [showAggregateChart, setShowAggregateChart] = useState(false);
  const [sortBy, setSortBy] = useState<'uses' | 'lastUsed' | 'name'>('uses');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const dateFrom = useMemo(() => {
    if (dateRange === 'custom' && customStartDate) {
      return new Date(customStartDate).toISOString();
    }
    const now = new Date();
    switch (dateRange) {
      case 'last7days':
        now.setDate(now.getDate() - 7);
        break;
      case 'last30days':
        now.setDate(now.getDate() - 30);
        break;
      case 'last90days':
        now.setDate(now.getDate() - 90);
        break;
      default:
        now.setDate(now.getDate() - 30);
    }
    return now.toISOString();
  }, [dateRange, customStartDate]);

  const dateTo = useMemo(() => {
    if (dateRange === 'custom' && customEndDate) {
      return new Date(customEndDate).toISOString();
    }
    return new Date().toISOString();
  }, [dateRange, customEndDate]);

  const { data, loading, error } = useQuery(GET_TEAM_TEMPLATE_ANALYTICS, {
    variables: {
      teamId,
      dateFrom,
      dateTo,
      formats: selectedFormats.length === 3 ? null : selectedFormats,
    },
    skip: !isOpen || (dateRange === 'custom' && (!customStartDate || !customEndDate)),
    fetchPolicy: 'cache-and-network',
  });

  const sortedTemplates = useMemo(() => {
    if (!data?.teamTemplateAnalytics?.templates) return [];

    // Filter templates based on selected formats
    let templates = [...data.teamTemplateAnalytics.templates];
    
    if (selectedFormats.length > 0 && selectedFormats.length < 3) {
      templates = templates.filter(t => selectedFormats.includes(t.format));
    }
    
    templates.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'uses':
          comparison = a.totalUses - b.totalUses;
          break;
        case 'lastUsed':
          if (!a.lastUsedAt && !b.lastUsedAt) return 0;
          if (!a.lastUsedAt) return 1;
          if (!b.lastUsedAt) return -1;
          comparison = new Date(a.lastUsedAt).getTime() - new Date(b.lastUsedAt).getTime();
          break;
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    
    return templates;
  }, [data, sortBy, sortDirection, selectedFormats]);

  // Calculate filtered aggregate trend from filtered templates
  const filteredAggregateTrend = useMemo(() => {
    if (!sortedTemplates.length) return [];
    
    // Aggregate all usage trends from filtered templates
    const aggregated: Record<string, number> = {};
    
    sortedTemplates.forEach(template => {
      template.usageTrend.forEach(point => {
        aggregated[point.date] = (aggregated[point.date] || 0) + point.count;
      });
    });
    
    return Object.entries(aggregated)
      .map(([date, count]) => ({ date, count: count as number }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [sortedTemplates]);

  // Calculate filtered total usage
  const filteredTotalUsage = useMemo(() => {
    return sortedTemplates.reduce((sum, template) => sum + template.totalUses, 0);
  }, [sortedTemplates]);

  const handleSort = (column: 'uses' | 'lastUsed' | 'name') => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('desc');
    }
  };


  const handleExport = () => {
    if (data?.teamTemplateAnalytics?.templates) {
      exportTemplateAnalyticsToCsv(
        sortedTemplates,
        teamId,
        `${dateRange}-${new Date().toISOString().split('T')[0]}`
      );
    }
  };

  const formatRelativeTime = (dateStr: string | null): string => {
    if (!dateStr) return formatMessage({ id: 'label.never', defaultMessage: 'Never' });
    
    const date = dayjs(dateStr);
    const now = dayjs();
    const diffInMinutes = now.diff(date, 'minute');
    const diffInHours = now.diff(date, 'hour');
    const diffInDays = now.diff(date, 'day');
    
    if (diffInMinutes < 60) {
      return formatMessage(
        { id: 'label.minutesAgo', defaultMessage: '{count} minutes ago' },
        { count: diffInMinutes }
      );
    } else if (diffInHours < 24) {
      return formatMessage(
        { id: 'label.hoursAgo', defaultMessage: '{count} hours ago' },
        { count: diffInHours }
      );
    } else if (diffInDays === 1) {
      return formatMessage({ id: 'label.yesterday', defaultMessage: 'Yesterday' });
    } else if (diffInDays < 7) {
      return formatMessage(
        { id: 'label.daysAgo', defaultMessage: '{count} days ago' },
        { count: diffInDays }
      );
    } else {
      return date.format('MMM D, YYYY');
    }
  };

  const getFormatBadgeClass = (format: string): string => {
    switch (format) {
      case 'pdf':
        return styles.formatBadgePdf;
      case 'fully_editable':
        return styles.formatBadgeEditable;
      case 'restricted':
        return styles.formatBadgeRestricted;
      default:
        return '';
    }
  };

  const formatDisplayName = (format: string): string => {
    switch (format) {
      case 'pdf':
        return 'PDF';
      case 'fully_editable':
        return 'Fully Editable';
      case 'restricted':
        return 'Restricted';
      default:
        return format;
    }
  };

  return (
    <>
      <Slideout 
        isOpen={isOpen} 
        onClose={onClose} 
        width="large" 
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <span>Template Analytics</span>
            <Button 
              variant="outline-primary" 
              size="sm" 
              onClick={handleExport} 
              disabled={!data?.teamTemplateAnalytics?.templates?.length}
            >
              <FormattedMessage id="cta.exportCsv" defaultMessage="Export CSV" />
            </Button>
          </div>
        }
      >
        <div className={styles.analyticsHeader}>
          {/* Filters Section (Left - 1/3 width) */}
          <div className={styles.filtersSection}>
            <div className={styles.filterRow}>
              <div className={styles.dateRangeFilter}>
                <label className={styles.filterLabel}>
                  <FormattedMessage id="label.dateRange" defaultMessage="Date Range" />
                </label>
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  className={styles.dateRangeSelect}
                >
                  <option value="last7days">Last 7 days</option>
                  <option value="last30days">Last 30 days</option>
                  <option value="last90days">Last 90 days</option>
                  <option value="custom">Custom range</option>
                </select>
              </div>
              
              <div className={styles.formatFilter}>
                <label className={styles.filterLabel}>
                  <FormattedMessage id="label.format" defaultMessage="Format" />
                </label>
                <Dropdown className={styles.formatDropdown}>
                  <Dropdown.Toggle id="dropdown-filter-format" as={DropdownToggle}>
                    <FormattedMessage
                      id="label.format.readout"
                      defaultMessage={`{count, select,
                        0 {All Formats}
                        1 {{format, select,
                          pdf {PDF}
                          fully_editable {Fully Editable}
                          restricted {Restricted}
                          other {{format}}
                        }}
                        other {{count} formats}
                      }`}
                      values={{
                        count: selectedFormats.length === 3 ? 0 : selectedFormats.length,
                        format: selectedFormats.length === 1 ? selectedFormats[0] : ''
                      }}
                    />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <DropdownItem highlightable>
                      <Checkbox
                        id="dropdown-filter-format-all"
                        checked={selectedFormats.length === 3}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                          if (event.currentTarget.checked) {
                            setSelectedFormats(['pdf', 'fully_editable', 'restricted']);
                          } else {
                            setSelectedFormats([]);
                          }
                        }}
                        label="All Formats"
                      />
                    </DropdownItem>
                    <DropdownBody>
                      <DropdownItem highlightable>
                        <Checkbox
                          id="dropdown-filter-format-pdf"
                          value="pdf"
                          checked={selectedFormats.includes('pdf')}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            const value = event.currentTarget.value;
                            if (event.currentTarget.checked) {
                              setSelectedFormats([...selectedFormats, value]);
                            } else {
                              setSelectedFormats(selectedFormats.filter(f => f !== value));
                            }
                          }}
                          label="PDF"
                        />
                      </DropdownItem>
                      <DropdownItem highlightable>
                        <Checkbox
                          id="dropdown-filter-format-fully-editable"
                          value="fully_editable"
                          checked={selectedFormats.includes('fully_editable')}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            const value = event.currentTarget.value;
                            if (event.currentTarget.checked) {
                              setSelectedFormats([...selectedFormats, value]);
                            } else {
                              setSelectedFormats(selectedFormats.filter(f => f !== value));
                            }
                          }}
                          label="Fully Editable"
                        />
                      </DropdownItem>
                      <DropdownItem highlightable>
                        <Checkbox
                          id="dropdown-filter-format-restricted"
                          value="restricted"
                          checked={selectedFormats.includes('restricted')}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            const value = event.currentTarget.value;
                            if (event.currentTarget.checked) {
                              setSelectedFormats([...selectedFormats, value]);
                            } else {
                              setSelectedFormats(selectedFormats.filter(f => f !== value));
                            }
                          }}
                          label="Restricted"
                        />
                      </DropdownItem>
                    </DropdownBody>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
            
            {dateRange === 'custom' && (
              <div className={styles.customDateRange}>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  max={customEndDate || dayjs().format('YYYY-MM-DD')}
                  placeholder="Start"
                  className={styles.dateInput}
                />
                <span className={styles.dateSeparator}>to</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  min={customStartDate}
                  max={dayjs().format('YYYY-MM-DD')}
                  placeholder="End"
                  className={styles.dateInput}
                />
              </div>
            )}
          </div>

          {/* Summary Chart Section (Right - 2/3 width) */}
          <div className={styles.chartSection}>
            {filteredAggregateTrend.length > 0 && (
              <div 
                className={styles.miniChart}
                onClick={() => setShowAggregateChart(true)}
                title="Click to enlarge"
              >
                <ResponsiveContainer width="100%" height={120}>
                  <LineChart data={filteredAggregateTrend}>
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="#007bff"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Tooltip
                      formatter={(value: any) => [`${value} uses`, '']}
                      labelFormatter={(label: any) => dayjs(label).format('MMM D')}
                      contentStyle={{ fontSize: '12px' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
                <div className={styles.totalUsage}>
                  Total: {filteredTotalUsage} uses
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Templates Table */}
        <div className={styles.tableContainer}>
          {loading ? (
            <div className={styles.loadingContainer}>
              <LoadingSpinner />
            </div>
          ) : error ? (
            <div className={styles.errorContainer}>
              <FormattedMessage
                id="error.loadingAnalytics"
                defaultMessage="Error loading analytics. Please try again."
              />
            </div>
          ) : (
            <table className={styles.analyticsTable}>
              <thead>
                <tr>
                  <th onClick={() => handleSort('name')} className={styles.sortable}>
                    <FormattedMessage id="label.templateName" defaultMessage="Template Name" />
                    {sortBy === 'name' && (
                      <span className={styles.sortIndicator}>
                        {sortDirection === 'desc' ? ' ↓' : ' ↑'}
                      </span>
                    )}
                  </th>
                  <th>
                    <FormattedMessage id="label.format" defaultMessage="Format" />
                  </th>
                  <th onClick={() => handleSort('uses')} className={styles.sortable}>
                    <FormattedMessage id="label.uses" defaultMessage="Uses" />
                    {sortBy === 'uses' && (
                      <span className={styles.sortIndicator}>
                        {sortDirection === 'desc' ? ' ↓' : ' ↑'}
                      </span>
                    )}
                  </th>
                  <th onClick={() => handleSort('lastUsed')} className={styles.sortable}>
                    <FormattedMessage id="label.lastUsed" defaultMessage="Last Used" />
                    {sortBy === 'lastUsed' && (
                      <span className={styles.sortIndicator}>
                        {sortDirection === 'desc' ? ' ↓' : ' ↑'}
                      </span>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedTemplates.map((template) => (
                  <tr
                    key={template.id}
                    onClick={() => setSelectedTemplate(template)}
                    className={styles.clickableRow}
                  >
                    <td>{template.name}</td>
                    <td>
                      <span className={`${styles.formatBadge} ${getFormatBadgeClass(template.format)}`}>
                        {formatDisplayName(template.format)}
                      </span>
                    </td>
                    <td>{template.totalUses}</td>
                    <td>{formatRelativeTime(template.lastUsedAt)}</td>
                  </tr>
                ))}
                {sortedTemplates.length === 0 && (
                  <tr>
                    <td colSpan={4} className={styles.noData}>
                      <FormattedMessage
                        id="message.noTemplateData"
                        defaultMessage="No template usage data for the selected period"
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </Slideout>

      {/* Template Chart Modal */}
      {selectedTemplate && (
        <TemplateChartModal
          isOpen={!!selectedTemplate}
          onClose={() => setSelectedTemplate(null)}
          template={selectedTemplate}
        />
      )}
      
      {/* Aggregate Chart Modal */}
      {showAggregateChart && filteredAggregateTrend.length > 0 && (
        <TemplateChartModal
          isOpen={showAggregateChart}
          onClose={() => setShowAggregateChart(false)}
          template={{
            id: 'aggregate',
            name: selectedFormats.length === 3 ? 'All Templates' : `Filtered Templates (${selectedFormats.map(formatDisplayName).join(', ')})`,
            format: 'all',
            totalUses: filteredTotalUsage,
            lastUsedAt: null,
            usageTrend: filteredAggregateTrend,
          }}
        />
      )}
    </>
  );
};

export default TemplateAnalyticsSlideout;